import axios, { AxiosInstance } from 'axios'

/**
 * API 클라이언트
 * Axios 인스턴스 설정 및 기본 API 메서드
 */

// import.meta.env 타입 오류 방지: vite-env.d.ts에 타입 선언 필요
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api/v1'

// Axios 인스턴스 생성
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 요청 인터셉터 (JWT 토큰 + API Key 추가)
apiClient.interceptors.request.use(
  (config) => {
    // JWT 토큰 우선
    const token = localStorage.getItem('u3p_token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    // API Key도 함께 전송
    const apiKey = localStorage.getItem('u3p_api_key')
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 응답 인터셉터 (에러 처리)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰 만료 또는 무효
      localStorage.removeItem('u3p_token')
      localStorage.removeItem('u3p_api_key')
      localStorage.removeItem('u3p_user')
      // 로그인 페이지가 아닐 때만 리다이렉트
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ============================================================================
// Platform API
// ============================================================================

export const platformsApi = {
  /**
   * 플랫폼 목록 조회
   */
  list: () => apiClient.get('/platforms'),

  /**
   * 플랫폼 생성
   */
  create: (data: {
    name: string
    slug: string
    description?: string
    websiteUrl?: string
    logoUrl?: string
    supportEmail?: string
  }) => apiClient.post('/platforms', data),

  /**
   * 플랫폼 조회
   */
  getById: (id: string) => apiClient.get(`/platforms/${id}`),

  /**
   * 플랫폼 수정
   */
  update: (id: string, data: Partial<any>) =>
    apiClient.patch(`/platforms/${id}`, data),

  /**
   * 플랫폼 삭제
   */
  delete: (id: string) => apiClient.delete(`/platforms/${id}`),

  /**
   * API Key 재생성
   */
  regenerateApiKey: (id: string) =>
    apiClient.post(`/platforms/${id}/regenerate-api-key`),
}

// ============================================================================
// Subscription Plans API
// ============================================================================

export const plansApi = {
  /**
   * 플랜 목록 조회
   */
  list: (platformId?: string) => {
    const params = platformId ? { platformId } : {}
    return apiClient.get('/plans', { params })
  },

  /**
   * 플랜 생성
   */
  create: (data: any, platformId?: string) => {
    const params = platformId ? { platformId } : {}
    return apiClient.post('/plans', data, { params })
  },

  /**
   * 플랜 조회
   */
  getById: (id: string, platformId?: string) => {
    const params = platformId ? { platformId } : {}
    return apiClient.get(`/plans/${id}`, { params })
  },

  /**
   * 플랜 수정
   */
  update: (id: string, data: Partial<any>, platformId?: string) => {
    const params = platformId ? { platformId } : {}
    return apiClient.patch(`/plans/${id}`, data, { params })
  },

  /**
   * 플랜 삭제
   */
  delete: (id: string, platformId?: string) => {
    const params = platformId ? { platformId } : {}
    return apiClient.delete(`/plans/${id}`, { params })
  },
}

// ============================================================================
// Subscriptions API
// ============================================================================

export const subscriptionsApi = {
  /**
   * 구독 목록 조회
   */
  list: (filters?: any) => apiClient.get('/subscriptions', { params: filters }),

  /**
   * 구독 생성
   */
  create: (data: any) => apiClient.post('/subscriptions', data),

  /**
   * 구독 조회
   */
  getById: (id: string) => apiClient.get(`/subscriptions/${id}`),

  /**
   * 구독 수정 (상태, 메타데이터 등)
   */
  update: (id: string, data: Partial<any>) =>
    apiClient.patch(`/subscriptions/${id}`, data),

  /**
   * 구독 취소
   */
  delete: (id: string, reason?: string) =>
    apiClient.delete(`/subscriptions/${id}`, { data: { reason } }),

  /**
   * 구독 취소 (backwards compatibility)
   */
  cancel: (id: string, reason?: string) =>
    apiClient.post(`/subscriptions/${id}/cancel`, { reason }),

  /**
   * 실패한 결제 재시도
   */
  retry: (id: string) =>
    apiClient.post(`/subscriptions/${id}/retry`),
}

// ============================================================================
// Payments API
// ============================================================================

export const paymentsApi = {
  /**
   * 결제 목록 조회
   */
  list: (filters?: any) => apiClient.get('/payments', { params: filters }),

  /**
   * 결제 조회
   */
  getById: (id: string) => apiClient.get(`/payments/${id}`),

  /**
   * 결제 환불
   */
  refund: (id: string, data?: { amount?: number; reason?: string }) =>
    apiClient.post(`/payments/${id}/refund`, data),

  /**
   * 구독의 결제 내역
   */
  getSubscriptionPayments: (subscriptionId: string, params?: any) =>
    apiClient.get(`/payments/subscription/${subscriptionId}`, { params }),

  /**
   * 결제 통계 - 개요
   */
  getStats: (period?: string) =>
    apiClient.get('/payments/stats/overview', { params: { period } }),

  /**
   * 결제 통계 - 일일 매출
   */
  getDailyRevenue: (days?: number) =>
    apiClient.get('/payments/stats/daily-revenue', { params: { days } }),

  /**
   * 결제 통계 - 수단별
   */
  getByMethod: () => apiClient.get('/payments/stats/by-method'),
}

// ============================================================================
// AGU API
// ============================================================================

export const aguApi = {
  /**
   * AGU 플랜 목록 조회
   */
  listPlans: (params?: { platformId?: string; planType?: string; status?: string; limit?: number; offset?: number }) =>
    apiClient.get('/agu-plans', { params }),

  /**
   * AGU 플랜 조회
   */
  getPlan: (id: string) =>
    apiClient.get(`/agu-plans/${id}`),

  /**
   * AGU 플랜 생성 (관리자용)
   */
  createPlan: (data: any) =>
    apiClient.post('/agu-plans', data),

  /**
   * AGU 플랜 수정 (관리자용)
   */
  updatePlan: (id: string, data: any) =>
    apiClient.patch(`/agu-plans/${id}`, data),

  /**
   * AGU 플랜 삭제 (관리자용, soft delete)
   */
  deletePlan: (id: string) =>
    apiClient.delete(`/agu-plans/${id}`),

  /**
   * AGU 포인트 구매
   */
  purchasePlan: (data: { platformId: string; customerId: string; planId: string; paymentMethod: string; paymentDataId?: string }) =>
    apiClient.post('/agu-plans/purchase', data),

  /**
   * AGU 지갑 조회
   */
  getWallet: (walletId: string) =>
    apiClient.get(`/agu-plans/wallet/${walletId}`),

  /**
   * AGU 지갑 조회 (customerId 기반)
   */
  getWalletByCustomer: (customerId: string) =>
    apiClient.get(`/agu/wallets/${customerId}`),

  /**
   * AGU 지갑 목록 (관리자/운영자용)
   */
  listWallets: (params: { platformId?: string; customerId?: string; search?: string }) =>
    apiClient.get('/agu/wallets', { params }),

  /**
   * AGU 보너스 조회
   */
  getBonuses: (walletId: string, status?: string) =>
    apiClient.get(`/agu-plans/bonus/${walletId}`, { params: status ? { status } : {} }),

  /**
   * AGU 통계 조회
   */
  getStats: () => apiClient.get('/agu/stats'),
}

// Stripe 결제 API 연동
export async function createStripePaymentIntent(amount: number, currency: string) {
  return await axios.post('/api/stripe/payment-intent', { amount, currency })
}

export async function retrieveStripePaymentIntent(id: string) {
  return await axios.get(`/api/stripe/payment-intent/${id}`)
}

// Crypto 결제 API 연동
export const cryptoApi = {
  /**
   * 암호화폐 가격 조회 (단일)
   */
  getPrice: (symbol: string) =>
    apiClient.get(`/crypto/prices/${symbol}`),

  /**
   * 암호화폐 가격 조회 (다중)
   */
  getPrices: (symbols: string[]) =>
    apiClient.get('/crypto/prices', { params: { symbols: symbols.join(',') } }),

  /**
   * 가격 변환
   */
  convertPrice: (data: { symbol: string; amount: number; direction: 'to-usd' | 'from-usd' }) =>
    apiClient.post('/crypto/prices', data),

  /**
   * 암호화폐 결제 초기화
   */
  initPayment: (data: { subscriptionId: string; walletAddress: string; amount: number; symbol: 'ETH' | 'MATIC' | 'BNB' | 'ADA' | 'SUI'; chain: 'ethereum' | 'polygon' | 'bsc' }) =>
    apiClient.post('/crypto/payment/init', data),

  /**
   * 트랜잭션 상태 확인
   */
  checkPayment: (data: { txHash: string; chain: 'ethereum' | 'polygon' | 'bsc' }) =>
    apiClient.post('/crypto/payment/check', data),

  /**
   * 결제 상태 조회
   */
  getPaymentStatus: (paymentId: string) =>
    apiClient.get(`/crypto/payment/${paymentId}`),
}

export async function sendCryptoTransaction(tx: any) {
  return await axios.post('/api/crypto/send', tx)
}

export async function getCryptoTransaction(hash: string) {
  return await axios.get(`/api/crypto/tx/${hash}`)
}

/**
 * Auth API — 로그인/회원가입/토큰갱신
 */
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
  register: (data: { email: string; password: string; name: string }) =>
    apiClient.post('/auth/register', data),
  refresh: () => apiClient.post('/auth/refresh'),
  me: () => apiClient.get('/auth/me'),
}

/**
 * 모든 API를 한곳에서 접근
 */
export const api = {
  auth: authApi,
  platforms: platformsApi,
  plans: plansApi,
  subscriptions: subscriptionsApi,
  payments: paymentsApi,
  crypto: cryptoApi,
  ...aguApi,
  agu: aguApi, // 하위 호환
  admin: {
    // 다중 API KEY 관리용
    listApiKeys: () => apiClient.get('/admin/api-keys'),
    createApiKey: () => apiClient.post('/admin/api-keys'),
    deleteApiKey: (id: string) => apiClient.delete(`/admin/api-keys/${id}`),
  },
}

export default api
